import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import ProGate from '../components/ProGate';
import { Scale, Plus, TrendingDown, TrendingUp, Target, Calendar, Trash2, Loader, Trophy, Sparkles, X, Award } from 'lucide-react';

const WeightPreview = () => (
  <div className="weight-preview">
    <div className="preview-stats">
      <div className="preview-stat"><span>75.2</span>kg actuel</div>
      <div className="preview-stat green"><span>-3.5</span>kg perdus</div>
      <div className="preview-stat"><span>70</span>kg objectif</div>
    </div>
  </div>
);

const WeightPage = () => {
  const { user, getSubscriptionStatus } = useAuth();
  const subscriptionStatus = getSubscriptionStatus();
  const isPro = subscriptionStatus === 'pro' || subscriptionStatus === 'trial';
  
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [goal, setGoal] = useState(null);
  const [showGoalModal, setShowGoalModal] = useState(false);

  const loadEntries = useCallback(async () => {
    if (!user || !isPro) return;
    setIsLoading(true);
    try {
      const { data } = await supabase
        .from('weight_entries')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .limit(90);
      setEntries(data || []);

      const { data: goalData } = await supabase
        .from('weight_goals')
        .select('*')
        .eq('user_id', user.id)
        .single();
      setGoal(goalData);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, isPro]);

  useEffect(() => {
    if (isPro) loadEntries();
    else setIsLoading(false);
  }, [loadEntries, isPro]);

  const currentWeight = entries[0]?.weight || null;
  const startWeight = entries[entries.length - 1]?.weight || currentWeight;
  const totalLost = startWeight && currentWeight ? (startWeight - currentWeight).toFixed(1) : 0;
  const goalWeight = goal?.target_weight || null;
  const remainingToGoal = goalWeight && currentWeight ? (currentWeight - goalWeight).toFixed(1) : null;

  const progressPercent = goalWeight && startWeight && currentWeight
    ? Math.min(100, Math.max(0, ((startWeight - currentWeight) / (startWeight - goalWeight)) * 100))
    : 0;

  const chartData = [...entries].reverse().slice(-30);
  const minWeight = chartData.length > 0 ? Math.min(...chartData.map(e => e.weight)) - 2 : 60;
  const maxWeight = chartData.length > 0 ? Math.max(...chartData.map(e => e.weight)) + 2 : 100;
  const weightRange = maxWeight - minWeight || 1;

  const getMotivation = () => {
    if (totalLost > 5) return { emoji: '🏆', text: 'Incroyable ! Plus de 5kg de perdus, tu es sur la bonne voie !' };
    if (totalLost > 2) return { emoji: '🔥', text: 'Super progression ! Continue comme ça, les résultats arrivent !' };
    if (totalLost > 0) return { emoji: '💪', text: 'Chaque gramme compte ! Tu avances dans la bonne direction.' };
    return { emoji: '🚀', text: 'Ajoute ta première pesée pour commencer ton suivi !' };
  };

  const motivation = getMotivation();

  const deleteEntry = async (id) => {
    if (!window.confirm('Supprimer cette entrée ?')) return;
    await supabase.from('weight_entries').delete().eq('id', id);
    loadEntries();
  };

  return (
    <ProGate
      feature="Ton Suivi du Poids Motivant"
      description="Chaque gramme perdu mérite d'être célébré. Avec notre suivi intelligent, tu verras enfin ta progression en temps réel, avec des prédictions qui te montrent exactement quand tu atteindras ton objectif. La motivation, enfin visuelle."
      benefits={[
        "Courbe d'évolution sur 30+ jours",
        "Prédiction de ta date d'objectif",
        "Messages de motivation personnalisés",
        "Célébration de chaque victoire"
      ]}
    >
      <div className="weight-page">
        <div className="page-header">
          <div>
            <h1>⚖️ Suivi du Poids</h1>
            <p>Visualise ta progression vers ton objectif</p>
          </div>
          <button className="primary-btn" onClick={() => setShowAddModal(true)}>
            <Plus size={20} />
            <span>Nouvelle pesée</span>
          </button>
        </div>

        {isLoading ? (
          <div className="loading-state"><Loader size={32} className="spin" /></div>
        ) : (
          <>
            <div className="stats-grid-4">
              <div className="stat-card blue">
                <Scale size={24} />
                <div className="stat-content">
                  <span className="stat-value">{currentWeight ? `${currentWeight}` : '--'}</span>
                  <span className="stat-label">kg actuel</span>
                </div>
              </div>
              <div className={`stat-card ${parseFloat(totalLost) >= 0 ? 'green' : 'red'}`}>
                {parseFloat(totalLost) >= 0 ? <TrendingDown size={24} /> : <TrendingUp size={24} />}
                <div className="stat-content">
                  <span className="stat-value">{parseFloat(totalLost) >= 0 ? `-${totalLost}` : `+${Math.abs(totalLost)}`}</span>
                  <span className="stat-label">kg depuis le début</span>
                </div>
              </div>
              <div className="stat-card orange clickable" onClick={() => setShowGoalModal(true)}>
                <Target size={24} />
                <div className="stat-content">
                  <span className="stat-value">{goalWeight || 'Définir'}</span>
                  <span className="stat-label">{goalWeight ? 'kg objectif' : 'ton objectif'}</span>
                </div>
              </div>
              <div className="stat-card purple">
                <Trophy size={24} />
                <div className="stat-content">
                  <span className="stat-value">{remainingToGoal || '--'}</span>
                  <span className="stat-label">kg restants</span>
                </div>
              </div>
            </div>

            {goalWeight && (
              <div className="progress-section">
                <div className="progress-header">
                  <span>Progression vers l'objectif</span>
                  <span className="progress-percent">{Math.round(progressPercent)}%</span>
                </div>
                <div className="progress-bar-large">
                  <div className="progress-fill" style={{ width: `${progressPercent}%` }} />
                </div>
                <div className="progress-markers">
                  <span>{startWeight} kg</span>
                  <span>{goalWeight} kg 🎯</span>
                </div>
              </div>
            )}

            <div className="motivation-banner">
              <span className="motivation-emoji">{motivation.emoji}</span>
              <p>{motivation.text}</p>
            </div>

            <div className="card">
              <h2>📈 Évolution sur 30 jours</h2>
              {chartData.length > 1 ? (
                <div className="weight-chart">
                  <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="chart-svg">
                    {goalWeight && goalWeight >= minWeight && goalWeight <= maxWeight && (
                      <line x1="0" y1={40 - ((goalWeight - minWeight) / weightRange) * 40} x2="100" y2={40 - ((goalWeight - minWeight) / weightRange) * 40} stroke="#10b981" strokeWidth="0.5" strokeDasharray="2,2" />
                    )}
                    <polyline
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      points={chartData.map((entry, i) => {
                        const x = (i / (chartData.length - 1)) * 100;
                        const y = 40 - ((entry.weight - minWeight) / weightRange) * 40;
                        return `${x},${y}`;
                      }).join(' ')}
                    />
                    {chartData.map((entry, i) => {
                      const x = (i / (chartData.length - 1)) * 100;
                      const y = 40 - ((entry.weight - minWeight) / weightRange) * 40;
                      return <circle key={i} cx={x} cy={y} r="1.2" fill="#3b82f6" />;
                    })}
                  </svg>
                  <div className="chart-y-labels">
                    <span>{maxWeight} kg</span>
                    <span>{minWeight} kg</span>
                  </div>
                </div>
              ) : (
                <div className="empty-state small">
                  <Scale size={40} />
                  <p>Ajoute au moins 2 pesées pour voir ton graphique</p>
                </div>
              )}
            </div>

            <div className="card">
              <h2>📋 Historique des pesées</h2>
              {entries.length === 0 ? (
                <div className="empty-state">
                  <Scale size={48} />
                  <h3>Aucune pesée enregistrée</h3>
                  <p>Commence à suivre ton poids pour voir ta progression !</p>
                  <button onClick={() => setShowAddModal(true)} className="primary-btn"><Plus size={18} />Ma première pesée</button>
                </div>
              ) : (
                <div className="entries-list">
                  {entries.slice(0, 10).map((entry, i) => {
                    const prevEntry = entries[i + 1];
                    const diff = prevEntry ? (entry.weight - prevEntry.weight).toFixed(1) : null;
                    return (
                      <div key={entry.id} className="entry-row">
                        <div className="entry-date">
                          <Calendar size={16} />
                          {new Date(entry.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </div>
                        <div className="entry-weight">{entry.weight} kg</div>
                        {diff && (
                          <div className={`entry-diff ${parseFloat(diff) <= 0 ? 'positive' : 'negative'}`}>
                            {parseFloat(diff) <= 0 ? diff : `+${diff}`} kg
                          </div>
                        )}
                        <button className="delete-btn" onClick={() => deleteEntry(entry.id)}><Trash2 size={14} /></button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {showAddModal && (
          <AddWeightModal onClose={() => setShowAddModal(false)} onSave={() => { loadEntries(); setShowAddModal(false); }} userId={user?.id} lastWeight={currentWeight} />
        )}

        {showGoalModal && (
          <GoalModal onClose={() => setShowGoalModal(false)} onSave={() => { loadEntries(); setShowGoalModal(false); }} userId={user?.id} currentGoal={goal} currentWeight={currentWeight} />
        )}
      </div>
    </ProGate>
  );
};

const AddWeightModal = ({ onClose, onSave, userId, lastWeight }) => {
  const [weight, setWeight] = useState(lastWeight || '');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!weight || isNaN(parseFloat(weight))) return;
    setIsSaving(true);
    try {
      await supabase.from('weight_entries').insert({ user_id: userId, weight: parseFloat(weight), date });
      onSave();
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⚖️ Nouvelle pesée</h2>
          <button onClick={onClose} className="modal-close-btn"><X size={24} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Poids (kg)</label>
            <input type="number" step="0.1" value={weight} onChange={e => setWeight(e.target.value)} placeholder="75.5" autoFocus className="large-input" />
          </div>
          <div className="form-group">
            <label>Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} max={new Date().toISOString().split('T')[0]} />
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="secondary-btn">Annuler</button>
          <button onClick={handleSave} disabled={!weight || isSaving} className="primary-btn">
            {isSaving ? <Loader size={18} className="spin" /> : <Plus size={18} />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

const GoalModal = ({ onClose, onSave, userId, currentGoal, currentWeight }) => {
  const [targetWeight, setTargetWeight] = useState(currentGoal?.target_weight || '');
  const [targetDate, setTargetDate] = useState(currentGoal?.target_date || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!targetWeight) return;
    setIsSaving(true);
    try {
      if (currentGoal) {
        await supabase.from('weight_goals').update({ target_weight: parseFloat(targetWeight), target_date: targetDate }).eq('id', currentGoal.id);
      } else {
        await supabase.from('weight_goals').insert({ user_id: userId, start_weight: currentWeight, target_weight: parseFloat(targetWeight), target_date: targetDate });
      }
      onSave();
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box small" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🎯 Mon objectif</h2>
          <button onClick={onClose} className="modal-close-btn"><X size={24} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Poids cible (kg)</label>
            <input type="number" step="0.1" value={targetWeight} onChange={e => setTargetWeight(e.target.value)} placeholder="70" className="large-input" />
          </div>
          <div className="form-group">
            <label>Date cible</label>
            <input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)} min={new Date().toISOString().split('T')[0]} />
          </div>
          {currentWeight && targetWeight && (
            <div className="goal-preview-box">
              <Award size={20} />
              <span>
                {parseFloat(targetWeight) < currentWeight 
                  ? `Objectif : perdre ${(currentWeight - parseFloat(targetWeight)).toFixed(1)} kg`
                  : `Objectif : prendre ${(parseFloat(targetWeight) - currentWeight).toFixed(1)} kg`}
              </span>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="secondary-btn">Annuler</button>
          <button onClick={handleSave} disabled={!targetWeight || isSaving} className="primary-btn">
            {isSaving ? <Loader size={18} className="spin" /> : <Target size={18} />}
            Définir
          </button>
        </div>
      </div>
    </div>
  );
};

export default WeightPage;
